import type { EncodedID } from '@compassdigital/id/interface';

export interface BasePrivateConfig {
  payment?: PaymentConfig;
  loyalty?: {
    loyalty_emails: [];
    enabled: boolean;
  };
  apex?: {
    apex_integrated: boolean;
    apex_client_numb: string;
  };
  mealplan?: MealPlan[];
  supports?: {
    reorder: boolean;
  };
  meal_periods?: {
    label: string;
    start: string;
    end: string;
  }[];
  email?: string;
  kds?: {
    admin_passcode: string;
  };
  delivery_fee?: {
    type: string;
    value: number;
  };
  web_order?: {
    enabled: boolean;
  };
  excluded_payment_methods?: {
    credit_card: boolean;
    digital_wallet_pay: string[];
    cashless: string[];
    mealplan: string[];
    meal_swipes: string[];
    meal_exchange: string[];
  };
  keystore?: KeystoreConfig;
  is_checkin_enabled?: boolean;
}

export interface SitePrivateConfig extends BasePrivateConfig {
  // Add additional site private configuration properties here
}

export interface ServiceFee {
  /**
   * Type of service fee, eg 'dollar'
   */
  type: string;
  value: number;
}

export interface StationPrivateConfig extends BasePrivateConfig {
  // Add additional station private configuration properties here
  service_fee?: ServiceFee;
  tax_rate?: number;
}

export interface KeystoreConfig {
  payment?: PaymentConfig;
  mealplan?: MealplanKeystore;
  location?: {
    agilysys_client_cert?: string;
  };
}

export interface MealplanKeystore {
  uuid?: string; // CBORD
  auth_url?: string; // CBORD
  name?: string; // CBORD
  type?: string; // CBORD
  auth_type?: number; // CBORD
  short_name?: string; // CBORD
  program_name?: string; // CBORD
  primary_color?: string; // CBORD
}

export interface StationPublicConfig {
  logos?: {
    print?: string;
  };
  canadian_calorie_disclaimer?: string;
  calorie_disclaimer?: {
    en?: string;
    fr?: string;
  };
  display_calories?: boolean;
  delivery_destinations?: string[];
  delivery_destinations_automatic_timeslot?: string[];
  has_kds?: boolean;
  get_phone_number?: boolean;
  pickup_asap?: {
    enabled?: boolean;
    max?: number | null;
  };
  show_instructions?: boolean;
  show_single_timeslot?: boolean;
  runner_app_enabled?: boolean;
  mealplan?: Partial<MealPlan>[];
  cashless: { id: EncodedID };
}

/**
 * Configuration for payment processing.
 * This interface supports two mutually exclusive configurations:
 * 1. Actual payment configuration
 * 2. Encrypted configuration via is_encrypted and data fields
 */
export interface PaymentConfig {
  type?: string;
  // Only present under `keystore.payment` when keystore is decrypted
  freedompay?: FreedomPay;
  braintree?: {
    id: string;
    merchant_account_id: string;
  };
  refund?: boolean;
  /**
   * Flag indicating if the payment configuration is encrypted
   * When true, the actual configuration is stored in the data field
   * and other configuration fields should be ignored
   */
  is_encrypted?: boolean;

  /**
   * Encrypted payment configuration data
   * Only used when is_encrypted is true
   * Contains the encrypted version of the payment configuration
   */
  data?: string;
}

export interface PaymentConfigValidation {
  store_id: string;
  terminal_id: string;
}

export interface FreedomPay {
  id?: string;
  badge_pay_system_key?: string;
  freedompay_store_id?: string;
  freedompay_terminal_id?: string;
}

export interface MealPlan extends MealplanConfig {
  keystore?: Keystore;
  config?: AtriumConfig; // Atrium
  note?: MealPlanNote; // TouchNet and Atrium
  tax_exempt?: boolean; // TouchNet
  version?: number; // Atrium
}

export interface Keystore {
  certificate?: Certificate; // ITC
  device_id?: number; // ITC
  endpoint?: string; // ITC
  vendor_id?: string; // ITC
  webview?: string; // ITC / Atrium SSO
  key?: string; // Atrium
  API_URL?: string; // Atrium
  client?: string; // Atrium SSO
  api_url?: string; // TouchNet
  operator_id?: string; // TouchNet
  operator_password?: string; // TouchNet
  host?: string; // Blackboard
  aes_key?: string; // Blackboard
  port?: number; // Blackboard
  vendor_number?: number; // Blackboard
  manual_id_entry?: string; // Blackboard
  auth_url?: string; // CBORD
  uuid?: string; // CBORD
}

export interface Certificate {
  // ITC
  encoded_file?: string;
  name?: string;
  password?: string;
}

export interface AtriumConfig {
  // Atrium
  terminal_id?: string;
  version?: string;
  tenders?: ConfigTender[];
}

export interface TransactConfig {
  // Blackboard
  terminal_id?: string | string[];
}

export interface MealPlanNote {
  // Atrium and TouchNet
  [key: string]: string;
}

export interface ConfigTender {
  id?: string;
  name?: string;
  max_swipes_per_transaction?: number;
  tax_exempt?: boolean;
  tender_number?: EncodedID;
  type?: TenderTypes;
  // TODO: tender_type only exists on Shoppingcart MEQ payment methods and cashless tenders - refactor
  tender_type?: string;
  rates?: TenderRate[];
}

export interface TenderRate {
  amount?: number;
  startTime?: Date;
  endTime?: Date;
  hours?: string;
}

export enum TenderTypes {
  DECLINING_BALANCE = 'declining_balance',
  MEAL_EXCHANGE = 'meal_exchange',
  MEAL_EQUIVALENT = 'meal_equivalent',
}

export const enum MealplanTypes {
  ATRIUM = 'ATRIUM',
  ATRIUM_SSO = 'ATRIUM_SSO',
  TRANSACT_BLACKBOARD = 'BLACKBOARD',
  TRANSACT_BLACKBOARD_SAAS = 'BLACKBOARD SAAS',
  HEARTLAND = 'HEARTLAND',
  ITC_SSO = 'ITCSSO',
  ITC_STANDARD = 'ITCSTANDARD',
  ITC_LUNCHBOX = 'ITCLUNCHBOX',
  ITC_DIETARY_RESTRICTION = 'ITCDIETARYRESTRICTION',
  CBORD_WEB = 'CBORD',
  CBORD_DIRECT = 'CBORD DIRECT',
}

export const enum MealplanProviders {
  ATRIUM = 'atrium',
  TRANSACT_BLACKBOARD = 'blackboard',
  HEARTLAND = 'heartland',
  ITC = 'itc',
  CBORD_WEB = 'cbord',
}

export const MealplanTypeToProvider: Record<MealplanTypes, MealplanProviders> = {
  [MealplanTypes.ATRIUM]: MealplanProviders.ATRIUM,
  [MealplanTypes.ATRIUM_SSO]: MealplanProviders.ATRIUM,
  [MealplanTypes.TRANSACT_BLACKBOARD]: MealplanProviders.TRANSACT_BLACKBOARD,
  [MealplanTypes.TRANSACT_BLACKBOARD_SAAS]: MealplanProviders.TRANSACT_BLACKBOARD,
  [MealplanTypes.HEARTLAND]: MealplanProviders.HEARTLAND,
  [MealplanTypes.ITC_SSO]: MealplanProviders.ITC,
  [MealplanTypes.ITC_STANDARD]: MealplanProviders.ITC,
  [MealplanTypes.ITC_LUNCHBOX]: MealplanProviders.ITC,
  [MealplanTypes.ITC_DIETARY_RESTRICTION]: MealplanProviders.ITC,
  [MealplanTypes.CBORD_WEB]: MealplanProviders.CBORD_WEB,
  [MealplanTypes.CBORD_DIRECT]: MealplanProviders.CBORD_WEB,
};

export interface BadgePayConfig {
  id: string;
  type: string;
  name: string;
  webview: string;
  tax_exempt?: boolean;
}

export interface CashlessConfig {
  id: string;
  type: string;
  name: string;
  webview: string;
  voucher_webview: string;
  tenders: ConfigTender[];
  cashlessTenders?: ConfigTender[]; // Used for form validation
  freedompay?: FreedomPayKeys;
}

export interface FreedomPayKeys {
  badge_pay_system_key?: string;
}

export interface MealplanConfig {
  id: string;
  name: string;
  tenders?: ConfigTender[];
  type: MealplanTypes;
  note?: MealPlanNote;
  public?: Record<string, any>;
  valid_email_domains?: string[];
  terminal_id?: string | string[];
  isBrandMEQ?: boolean;
  isBrandMX?: boolean;
}
export interface SitePublicConfig {
  promotions: Record<string, any>[];
  badge_pay: BadgePayConfig;
  cashless: CashlessConfig;
  mealplan: Partial<MealPlan>[];
}

export interface SoftwareIntegrations {
  system365?: string;
  jwo?: string;
  nextep?: {
    mobile_app?: boolean;
    web?: boolean;
    order_streaming_enabled?: boolean;
  };
  agilysys?: {
    order_streaming_enabled?: boolean;
  };
  agilysys_dod?: {
    enabled: boolean;
  };
  time2eat?: {
    enabled: boolean;
  };
  mashgin?: {
    enabled: boolean;
  };
  centric_hub?: {
    enabled: boolean;
  };
  transact?: {
    enabled: boolean;
  };
  starship?: {
    enabled: boolean;
  };
  grub_hub?: {
    enabled: boolean;
  };
}

// device_mapping for integration partners
// new integration partners should use lowercase with underscores
export enum IntegrationPartner {
  AGILYSYS = 'Agilysys',
  AGILYSYS_DOD = 'agilysys_dod',
  BOOST = 'Boost',
  CENTRIC_HUB = 'Centric Hub',
  COMPASS_KDS = 'CDL',
  JWO = 'JWO',
  MASHGIN = 'Mashgin',
  NEXTEP = 'Nextep',
  SAVOUR = 'Savour',
  SCAN_AND_PAY = 'Scan & Pay',
  STANDARD_AI = 'Standard AI',
  THRIVE = 'Thrive',
  TIME2EAT = 'Time2Eat',
  TRANSACT = 'Transact',
  STARSHIP = 'starship',
  VOLANTE = 'Volante',
  GRUB_HUB = 'grub_hub',
}

// IntegrationPartner enum unchanged for backward compatibility
export const IntegrationPartnerLabel: Record<IntegrationPartner, string> = {
  [IntegrationPartner.AGILYSYS]: 'Agilysys',
  [IntegrationPartner.AGILYSYS_DOD]: 'Agilysys DoD',
  [IntegrationPartner.BOOST]: 'Boost',
  [IntegrationPartner.CENTRIC_HUB]: 'Centric Hub',
  [IntegrationPartner.COMPASS_KDS]: 'Compass KDS',
  [IntegrationPartner.JWO]: 'JWO',
  [IntegrationPartner.MASHGIN]: 'Mashgin',
  [IntegrationPartner.NEXTEP]: 'Nextep',
  [IntegrationPartner.SAVOUR]: 'Savour',
  [IntegrationPartner.SCAN_AND_PAY]: 'Scan & Pay',
  [IntegrationPartner.STANDARD_AI]: 'Standard AI',
  [IntegrationPartner.THRIVE]: 'Thrive',
  [IntegrationPartner.TIME2EAT]: 'Time2Eat',
  [IntegrationPartner.TRANSACT]: 'Transact',
  [IntegrationPartner.STARSHIP]: 'Starship',
  [IntegrationPartner.VOLANTE]: 'Volante',
  [IntegrationPartner.GRUB_HUB]: 'Grubhub',
};
